'use client';

import dayjs from 'dayjs';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useRouter } from '../routes/hooks';
//
import { toast } from 'sonner';

import { allLangs } from './all-langs';
import { fallbackLng, changeLangMessages as messages, cookieName } from './config-locales';

import type { LanguageValue } from './config-locales';

// ...existing code...

export function useTranslate(ns?: string) {
  const router = useRouter();
  const { t, i18n, ready } = useTranslation(ns);
  const [isChanging, setIsChanging] = useState(false);
  const changingRef = new Map<string, boolean>();

  const fallback = allLangs.filter((lang) => lang.value === fallbackLng)[0];

  const currentLang = allLangs.find((lang) => lang.value === i18n.resolvedLanguage);

  // Sync dayjs locale when language changes
  useEffect(() => {
    if (currentLang && ready) {
      dayjs.locale(currentLang.adapterLocale);
    }
  }, [currentLang, ready]);

  const onChangeLang = useCallback(
    async (newLang: LanguageValue) => {
      if (changingRef.has('active') || newLang === i18n.resolvedLanguage) return;

      setIsChanging(true);
      changingRef.set('active', true);
      
      try {
        // CRITICAL: Save to localStorage BEFORE changing language
        try {
          localStorage.setItem('i18nextLng', newLang);
        } catch (_) {
          // Storage may be unavailable in some environments
        }

        const langChangePromise = i18n.changeLanguage(newLang);
        const currentMessages = messages[newLang] || messages.en;

        toast.promise(langChangePromise, {
          loading: currentMessages.loading,
          success: () => currentMessages.success,
          error: currentMessages.error,
        });

        // Update dayjs locale with the NEW language
        const newLangObj = allLangs.find((lang) => lang.value === newLang);
        if (newLangObj) {
          dayjs.locale(newLangObj.adapterLocale);
        }

        // Wait for language change to complete
        await langChangePromise;

        // Update HTML lang attribute
        if (typeof document !== 'undefined') {
          document.documentElement.lang = newLang;
          document.documentElement.dir = newLang === 'ar' ? 'rtl' : 'ltr';
        }
        
        // Set cookie for server-side language detection
        try {
          document.cookie = `${cookieName}=${newLang}; path=/; max-age=31536000; SameSite=Lax`;
        } catch (_) {
          // Cookie may not be available in some environments
        }
      } catch (error) {
        console.error('Language change error:', error);
        toast.error(messages[newLang]?.error || 'Error changing language');
      } finally {
        setIsChanging(false);
        changingRef.delete('active');
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [i18n, router]
  );

  return {
    t,
    i18n,
    onChangeLang,
    currentLang: currentLang ?? fallback,
    isReady: ready && !isChanging,
  };
}
